import '../Admin/CreateQuestion.css';
import './CreateQuestionModal.css';
import { useState } from 'react';
import MultichoiceForm from '../Admin/QuestionForms/MultichoiceForm';
import MultipleCorrectForm from '../Admin/QuestionForms/MultipleCorrectForm';
import FillInForm from '../Admin/QuestionForms/FillInForm';
import TimelineForm from '../Admin/QuestionForms/TimelineForm';
import LinkingForm from '../Admin/QuestionForms/LinkingForm';
import FindInTextForm from '../Admin/QuestionForms/FindInTextForm';
import {
    createMultichoiceQuestion,
    createMultipleCorrectQuestion,
    createFillInQuestion,
    createTimelineQuestion,
    createLinkingQuestion,
    createFindInTextQuestion
} from '../../services/questionService';

const QUESTION_TYPES = [
    {
        id: 'Multichoice',
        name: 'Výběr z možností',
        description: 'Otázka s jednou správnou odpovědí',
        icon: '☑️'
    },
    {
        id: 'MultipleCorrect',
        name: 'Více správných odpovědí',
        description: 'Otázka s několika správnými odpověďmi',
        icon: '✅'
    },
    {
        id: 'FillIn',
        name: 'Doplňovačka',
        description: 'Otázka, kde uživatel vyplní odpověď',
        icon: '✏️'
    },
    {
        id: 'Linking',
        name: 'Propojovačka',
        description: 'Spojování položek ze dvou sloupců',
        icon: '🔗'
    },
    {
        id: 'Timeline',
        name: 'Časová osa',
        description: 'Seřazení událostí chronologicky',
        icon: '📅'
    },
    {
        id: 'FindInText',
        name: 'Najít v textu',
        description: 'Označit slovo nebo skupinu slov v textu',
        icon: '🔍'
    }
];

function CreateQuestionModal({ onQuestionCreated, onClose }) {
    const [selectedType, setSelectedType] = useState(null);

    const handleTypeSelect = (typeId) => {
        setSelectedType(typeId);
    };

    const handleBack = () => {
        if (selectedType) {
            setSelectedType(null);
        } else {
            onClose();
        }
    };

    const handleSubmit = async (questionData) => {
        try {
            let backendData;
            let createFunction;

            switch (questionData.type) {
                case 'Multichoice':
                    const correctIndex = questionData.choices.findIndex(
                        choice => choice.id === questionData.correctAnswer
                    );
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        choices: questionData.choices.map(choice => ({
                            text: choice.text,
                            notes: choice.note
                        })),
                        correctChoice: correctIndex
                    };
                    createFunction = createMultichoiceQuestion;
                    break;

                case 'MultipleCorrect':
                    const correctIndices = questionData.correctAnswers.map(answerId =>
                        questionData.choices.findIndex(choice => choice.id === answerId)
                    );
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        choices: questionData.choices.map(choice => ({
                            text: choice.text,
                            notes: choice.note
                        })),
                        correctChoices: correctIndices,
                        correctCount: questionData.correctCount || null
                    };
                    createFunction = createMultipleCorrectQuestion;
                    break;

                case 'FillIn':
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        text: questionData.text,
                        blankPosition: questionData.blankPosition,
                        correctAnswers: questionData.correctAnswers
                    };
                    createFunction = createFillInQuestion;
                    break;

                case 'Timeline':
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        items: questionData.items,
                        correctOrder: questionData.correctOrder
                    };
                    createFunction = createTimelineQuestion;
                    break;

                case 'Linking':
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        leftItems: questionData.leftItems,
                        rightItems: questionData.rightItems,
                        correctPairs: questionData.correctPairs
                    };
                    createFunction = createLinkingQuestion;
                    break;

                case 'FindInText':
                    backendData = {
                        title: questionData.questionText,
                        authorIds: questionData.relatedAuthors || [],
                        readingJournalIds: questionData.relatedBooks || [],
                        text: questionData.text,
                        correctIndices: questionData.correctIndices
                    };
                    createFunction = createFindInTextQuestion;
                    break;

                default:
                    throw new Error('Neznámý typ otázky');
            }

            const createdQuestion = await createFunction(backendData);
            onQuestionCreated(createdQuestion);
        } catch (error) {
            console.error('Error creating question:', error);
            alert(`Nepodařilo se vytvořit otázku: ${error.message}`);
        }
    };

    const handleCancel = () => {
        setSelectedType(null);
    };

    return (
        <div className="create-question-modal-overlay">
            <div className="create-question-modal-content">
                <button className="btn-back" onClick={handleBack}>
                    ← {selectedType ? 'Zpět na výběr typu' : 'Zpět na kvíz'}
                </button>

                <h1 className="page-title">Vytvořit otázku</h1>

                {!selectedType ? (
                    <>
                        <p className="page-subtitle">Vyberte typ otázky, kterou chcete vytvořit</p>

                        <div className="question-types-grid">
                            {QUESTION_TYPES.map(type => (
                                <div
                                    key={type.id}
                                    className="question-type-card"
                                    onClick={() => handleTypeSelect(type.id)}
                                >
                                    <div className="type-icon">{type.icon}</div>
                                    <h2 className="type-name">{type.name}</h2>
                                    <p className="type-description">{type.description}</p>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="question-form-container">
                        <div className="selected-type-header">
                            <span className="selected-type-icon">
                                {QUESTION_TYPES.find(t => t.id === selectedType)?.icon}
                            </span>
                            <h2 className="selected-type-name">
                                {QUESTION_TYPES.find(t => t.id === selectedType)?.name}
                            </h2>
                        </div>

                        {selectedType === 'Multichoice' && (
                            <MultichoiceForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                        {selectedType === 'MultipleCorrect' && (
                            <MultipleCorrectForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                        {selectedType === 'FillIn' && (
                            <FillInForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                        {selectedType === 'Timeline' && (
                            <TimelineForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                        {selectedType === 'Linking' && (
                            <LinkingForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                        {selectedType === 'FindInText' && (
                            <FindInTextForm onSubmit={handleSubmit} onCancel={handleCancel} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default CreateQuestionModal;
